import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../components/Toast';

// Context value is the showToast function itself (not wrapped in an object)
// so consumers can call it directly: const showToast = useToast();
const ToastContext = createContext(null);

// Provider — mounted at the App.js root, outside <Router> so toasts survive
// route transitions. Owns the toast queue and renders the fixed-position
// container that stacks active toasts in the top-right corner.
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback((message, type = 'info') => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // The toast container is portaled into document.body so it lives as a
  // direct sibling of any modal portals (ModalBackdrop), guaranteeing the
  // stacking-context hierarchy is flat and z-index comparison works: toast
  // container at 10000 > ModalBackdrop at 9999 regardless of where in the
  // React tree the ToastProvider sits. Without the portal, the container
  // would be nested inside #root and depend on subtle ancestor stacking
  // behavior to render above modals that ARE portaled out.
  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="toast-container" style={{ zIndex: 11000 }} aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <Toast
              key={t.id}
              message={t.message}
              type={t.type}
              onDismiss={() => dismissToast(t.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

// Hook — returns the showToast function directly.
// Usage: const showToast = useToast(); showToast('Saved!', 'success');
export function useToast() {
  const showToast = useContext(ToastContext);
  if (!showToast) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return showToast;
}
